import React, { useState } from 'react';
import {
  Card,
  CardBody,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
  Progress,
  Badge,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { useDropzone } from 'react-dropzone';
import { parseSrt } from '../utils/srtParser';
import { toast } from 'react-toastify';
import HlsVideoPlayer from './HlsVideoPlayer';
import PortraitMediaFrame from './PortraitMediaFrame';
import ThumbnailPreviewFrame from './ThumbnailPreviewFrame';
import MediaTile from './MediaTile';

export type ModuleUploadStatus = 'idle' | 'uploading' | 'uploaded' | 'error';

export interface ModuleQuestionForm {
  order: number;
  prompt: string;
  instruction?: string;
}

export interface ModuleReflectionForm {
  title?: string;
  description?: string;
  bulletPoints?: string[];
}

export interface ModuleFormData {
  day: number;
  /** MongoDB subdocument _id — used to preserve media when days are reordered or removed. */
  serverModuleId?: string;
  title: string;
  shortDesc?: string;
  description?: string;
  transcript?: string;
  transcriptTimestamps?: number[];
  transcriptFileName?: string | null;
  file?: File | null;
  thumbnail?: File | null;
  thumbnailPreview?: string;
  fileUrl?: string;
  type?: 'audio' | 'video' | 'question';
  mediaStatus?: string;
  mediaErrorMessage?: string;
  mediaUploaded?: boolean;
  fileUploadStatus?: ModuleUploadStatus;
  fileUploadProgress?: number;
  fileUploadError?: string;
  thumbnailUploadStatus?: ModuleUploadStatus;
  thumbnailUploadProgress?: number;
  thumbnailUploadError?: string;
  removingTarget?: 'file' | 'thumbnail' | null;
  questions?: ModuleQuestionForm[];
  reflection?: ModuleReflectionForm;
}

interface ModuleItemCardProps {
  module: ModuleFormData;
  index: number;
  contentId?: string;
  onUpdate: (field: keyof ModuleFormData, value: any) => void;
  onUploadMedia?: (payload: { file?: File; thumbnail?: File }) => void;
  onRemoveMedia?: (target: 'file' | 'thumbnail') => void;
  onRemove: () => void;
  canRemove: boolean;
}

const mediaStatusLabel = (status?: string): { color: string; text: string } | null => {
  switch (status) {
    case 'pending':
      return { color: 'warning', text: 'Transcode queued' };
    case 'processing':
      return { color: 'info', text: 'Transcoding…' };
    case 'ready':
      return { color: 'success', text: 'Ready' };
    case 'failed':
      return { color: 'danger', text: 'Transcode failed' };
    default:
      return null;
  }
};

const ModuleItemCard: React.FC<ModuleItemCardProps> = ({
  module,
  index,
  contentId,
  onUpdate,
  onUploadMedia,
  onRemoveMedia,
  onRemove,
  canRemove,
}) => {
  const questions = module.questions || [];
  const reflection = module.reflection || { title: '', description: '', bulletPoints: [] };
  const bulletPoints = reflection.bulletPoints || [];
  const transcodeBadge = mediaStatusLabel(module.mediaStatus);
  const hasTitle = Boolean(module.title?.trim());
  const isTargetUploading = (target: 'file' | 'thumbnail') =>
    target === 'file'
      ? module.fileUploadStatus === 'uploading'
      : module.thumbnailUploadStatus === 'uploading';
  const isTargetBusy = (target: 'file' | 'thumbnail') =>
    module.removingTarget === target || isTargetUploading(target);
  const fileUploadDisabled = !hasTitle || isTargetBusy('file');
  const thumbnailUploadDisabled = !hasTitle || isTargetBusy('thumbnail');
  const hasMediaFile = Boolean(module.file || module.fileUrl);
  const hasThumbnail = Boolean(module.thumbnail || module.thumbnailPreview);
  const hasTranscript = Boolean(
    module.transcriptFileName ||
      module.transcript?.trim() ||
      (module.transcriptTimestamps && module.transcriptTimestamps.length > 0)
  );
  const [confirmRemove, setConfirmRemove] = useState<'file' | 'thumbnail' | null>(null);

  const clearTranscript = () => {
    onUpdate('transcriptFileName', null);
    onUpdate('transcript', '');
    onUpdate('transcriptTimestamps', []);
  };

  const triggerUpload = (payload: { file?: File; thumbnail?: File }) => {
    if (!hasTitle) {
      toast.info('Add a title for this day before uploading media.');
      return;
    }
    if (!contentId) {
      toast.info('Save content once to enable auto-upload for each day.');
      return;
    }
    onUploadMedia?.(payload);
  };

  const isServerBacked = (target: 'file' | 'thumbnail') =>
    Boolean(contentId) &&
    (target === 'file' ? Boolean(module.fileUrl) : Boolean(module.thumbnailPreview && !module.thumbnail));

  const requestRemove = (target: 'file' | 'thumbnail') => {
    if (isTargetBusy(target)) return;
    if (isServerBacked(target)) {
      setConfirmRemove(target);
      return;
    }
    onRemoveMedia?.(target);
  };

  const confirmRemoveMedia = () => {
    if (confirmRemove) {
      onRemoveMedia?.(confirmRemove);
      setConfirmRemove(null);
    }
  };

  const updateReflection = (field: keyof ModuleReflectionForm, value: unknown) => {
    onUpdate('reflection', { ...reflection, [field]: value });
  };

  const addBulletPoint = () => {
    updateReflection('bulletPoints', [...bulletPoints, '']);
  };

  const updateBulletPoint = (bpIndex: number, value: string) => {
    const updated = [...bulletPoints];
    updated[bpIndex] = value;
    updateReflection('bulletPoints', updated);
  };

  const removeBulletPoint = (bpIndex: number) => {
    updateReflection(
      'bulletPoints',
      bulletPoints.filter((_, i) => i !== bpIndex),
    );
  };

  const addQuestion = () => {
    const nextOrder = questions.length + 1;
    onUpdate('questions', [...questions, { order: nextOrder, prompt: '', instruction: '' }]);
  };

  const updateQuestion = (qIndex: number, field: 'prompt' | 'instruction', value: string) => {
    const updated = [...questions];
    updated[qIndex] = { ...updated[qIndex], [field]: value };
    onUpdate('questions', updated);
  };

  const removeQuestion = (qIndex: number) => {
    const updated = questions.filter((_, i) => i !== qIndex).map((q, i) => ({ ...q, order: i + 1 }));
    onUpdate('questions', updated);
  };

  const onModuleMediaDrop = (acceptedFiles: File[]) => {
    if (!hasTitle || acceptedFiles.length === 0) return;
    const droppedFile = acceptedFiles[0];
    const detectedType: 'audio' | 'video' = droppedFile.type.startsWith('video/') ? 'video' : 'audio';
    onUpdate('type', detectedType);
    onUpdate('file', droppedFile);
    triggerUpload({ file: droppedFile });
  };

  const onModuleThumbnailDrop = (acceptedFiles: File[]) => {
    if (!hasTitle || acceptedFiles.length === 0) return;
    const thumbnailFile = acceptedFiles[0];
    onUpdate('thumbnail', thumbnailFile);
    onUpdate('thumbnailPreview', URL.createObjectURL(thumbnailFile));
    triggerUpload({ thumbnail: thumbnailFile });
  };

  const {
    getRootProps: getFileProps,
    getInputProps: getFileInputProps,
    isDragActive: isFileDragActive,
    isDragAccept: isFileDragAccept,
    isDragReject: isFileDragReject,
  } = useDropzone({
    accept: {
      'audio/mpeg': ['.mp3'],
      'audio/mp4': ['.m4a'],
      'audio/wav': ['.wav'],
      'video/mp4': ['.mp4'],
      'video/quicktime': ['.mov'],
      'video/x-msvideo': ['.avi'],
    },
    maxFiles: 1,
    disabled: fileUploadDisabled,
    onDrop: onModuleMediaDrop,
  });

  const {
    getRootProps: getThumbnailProps,
    getInputProps: getThumbnailInputProps,
    isDragActive: isThumbnailDragActive,
    isDragAccept: isThumbnailDragAccept,
    isDragReject: isThumbnailDragReject,
  } = useDropzone({
    accept: { 'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'], 'image/webp': ['.webp'] },
    maxFiles: 1,
    disabled: thumbnailUploadDisabled,
    onDrop: onModuleThumbnailDrop,
  });

  const handleSrtFile = (srtFile: File) => {
    console.log('[SRT] ModuleItemCard upload started', { name: srtFile.name, size: srtFile.size, type: srtFile.type });
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = (reader.result as string) || '';
        const { transcript, transcriptTimestamps } = parseSrt(text);
        console.log('[SRT] ModuleItemCard parse success', { name: srtFile.name, timestampCount: transcriptTimestamps.length });
        onUpdate('transcript', transcript);
        onUpdate('transcriptTimestamps', transcriptTimestamps);
        onUpdate('transcriptFileName', srtFile.name);
      } catch (err) {
        console.warn('[SRT] ModuleItemCard parse failed', { name: srtFile.name, error: err });
        // Invalid SRT; leave transcript unchanged
      }
    };
    reader.onerror = () => console.error('[SRT] ModuleItemCard file read error', { name: srtFile.name });
    reader.readAsText(srtFile);
  };

  const { getRootProps: getTranscriptProps, getInputProps: getTranscriptInputProps } = useDropzone({
    accept: {
      'text/plain': ['.srt'],
      'application/x-subrip': ['.srt'],
      'text/x-srt': ['.srt'],
      'application/octet-stream': ['.srt'],
    },
    maxFiles: 1,
    onDrop: (acceptedFiles, rejectedFiles) => {
      console.log('[SRT] ModuleItemCard dropzone onDrop', { accepted: acceptedFiles.length, rejected: rejectedFiles.length, acceptedNames: acceptedFiles.map((f) => f.name) });
      if (acceptedFiles.length > 0) {
        handleSrtFile(acceptedFiles[0]);
      } else if (rejectedFiles.length > 0 && rejectedFiles[0].file.name.toLowerCase().endsWith('.srt')) {
        console.log('[SRT] ModuleItemCard: rejected by type but .srt extension, processing', { name: rejectedFiles[0].file.name });
        handleSrtFile(rejectedFiles[0].file);
      }
    },
  });

  return (
    <Card className="mb-3 border">
      <CardBody className="pb-2">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">
            <i className="ri-calendar-check-line me-2 text-primary"></i>
            Day {index + 1}
          </h6>
          {canRemove && (
            <Button color="danger" size="sm" outline onClick={onRemove}>
              <i className="ri-delete-bin-line"></i> Remove
            </Button>
          )}
        </div>
        <Row>
          <Col md={12}>
            <FormGroup>
              <Label>Title <span className="text-danger">*</span></Label>
              <Input
                type="text"
                placeholder="Module title"
                value={module.title}
                onChange={(e) => onUpdate('title', e.target.value)}
                required
              />
            </FormGroup>
          </Col>
          <Col md={12}>
            <FormGroup>
              <Label>Short Description</Label>
              <Input
                type="text"
                placeholder="Brief description"
                value={module.shortDesc}
                onChange={(e) => onUpdate('shortDesc', e.target.value)}
              />
            </FormGroup>
          </Col>
          <Col md={12}>
            <FormGroup>
              <Label>Description</Label>
              <Input
                type="textarea"
                rows={2}
                placeholder="Detailed description"
                value={module.description}
                onChange={(e) => onUpdate('description', e.target.value)}
              />
            </FormGroup>
          </Col>
          <>
          {(module.fileUploadStatus === 'uploading' ||
            module.thumbnailUploadStatus === 'uploading' ||
            module.fileUploadStatus === 'error' ||
            module.thumbnailUploadStatus === 'error') && (
            <Col md={12}>
              {module.fileUploadStatus === 'uploading' && (
                <div className="mb-3">
                  <div className="d-flex justify-content-between small mb-1">
                    <span className="text-muted">Uploading video…</span>
                    <span className="text-muted">{module.fileUploadProgress ?? 0}%</span>
                  </div>
                  <Progress value={module.fileUploadProgress ?? 0} color="primary" />
                </div>
              )}
              {module.thumbnailUploadStatus === 'uploading' && (
                <div className="mb-3">
                  <div className="d-flex justify-content-between small mb-1">
                    <span className="text-muted">Uploading thumbnail…</span>
                    <span className="text-muted">{module.thumbnailUploadProgress ?? 0}%</span>
                  </div>
                  <Progress value={module.thumbnailUploadProgress ?? 0} color="info" />
                </div>
              )}
              {module.fileUploadStatus === 'error' && module.fileUploadError && (
                <Alert color="danger" className="py-2 small mb-3">
                  Video upload failed: {module.fileUploadError}. Drop the file again or save to retry.
                </Alert>
              )}
              {module.thumbnailUploadStatus === 'error' && module.thumbnailUploadError && (
                <Alert color="danger" className="py-2 small mb-3">
                  Thumbnail upload failed: {module.thumbnailUploadError}. Drop the image again or save to retry.
                </Alert>
              )}
            </Col>
          )}
          <Col md={12} className="mb-1">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <span className="small fw-semibold text-muted">Day media</span>
              {hasMediaFile && (
                <Badge color="primary" className="fw-normal">
                  {module.type === 'video' ? 'Video' : module.type === 'audio' ? 'Audio' : 'Media'}
                </Badge>
              )}
              {transcodeBadge && (
                <Badge color={transcodeBadge.color} className="fw-normal">
                  {transcodeBadge.text}
                </Badge>
              )}
              <span className="small text-muted">Vertical video · any thumbnail shape</span>
              {!hasTitle && (
                <span className="small text-warning">
                  <i className="ri-information-line me-1" />
                  Add a title above to enable uploads
                </span>
              )}
            </div>
          </Col>
          {module.mediaStatus === 'failed' && (
            <Col md={12}>
              <Alert color="warning" className="py-2 small mb-2">
                <strong>Transcode failed.</strong>{' '}
                {module.mediaErrorMessage ||
                  'The video uploaded, but converting it to streaming (HLS) failed. Drop the video again to retry, or check AWS MediaConvert on the server.'}
              </Alert>
            </Col>
          )}
          <Col md={12}>
            <Row className="justify-content-center g-4 mb-1">
              <Col xs="auto">
            <MediaTile
              label="Video"
              iconClass="ri-smartphone-line"
              layout="portrait"
              hasContent={hasMediaFile}
              disabled={fileUploadDisabled}
              removeDisabled={isTargetBusy('file')}
              isRemoving={module.removingTarget === 'file'}
              isUploading={module.fileUploadStatus === 'uploading'}
              uploadProgress={module.fileUploadProgress ?? 0}
              uploadLabel="Uploading video…"
              emptyHint={hasTitle ? 'Drop vertical video' : 'Add a title first'}
              replaceHint={hasTitle ? 'Drop or click to replace' : 'Add a title first'}
              isDragActive={isFileDragActive}
              isDragAccept={isFileDragAccept}
              isDragReject={isFileDragReject}
              dragAcceptLabel="Drop video or audio"
              dragRejectLabel="Use video or audio only"
              getRootProps={getFileProps}
              getInputProps={getFileInputProps}
              onRemoveClick={hasMediaFile ? () => requestRemove('file') : undefined}
            >
              {module.file ? (
                <PortraitMediaFrame poster={module.thumbnailPreview}>
                  <div className="text-center px-2">
                    <i className="ri-film-line fs-2 text-white d-block mb-2" />
                    <p className="mb-0 text-white small text-truncate">{module.file.name}</p>
                    {!contentId && (
                      <Badge color="warning" className="mt-2 fw-normal">Pending save</Badge>
                    )}
                  </div>
                </PortraitMediaFrame>
              ) : module.fileUrl ? (
                <>
                  {module.type === 'audio' && (
                    <div className="w-100 px-2">
                      <i className="ri-music-2-line fs-2 text-primary d-block text-center mb-2" />
                      <audio controls className="w-100" onClick={(e) => e.stopPropagation()}>
                        <source src={module.fileUrl} />
                      </audio>
                    </div>
                  )}
                  {module.type === 'video' && (
                    <PortraitMediaFrame poster={module.thumbnailPreview}>
                      <HlsVideoPlayer
                        src={module.fileUrl}
                        poster={module.thumbnailPreview}
                        controls
                        fit="contain"
                        className="w-100 h-100"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </PortraitMediaFrame>
                  )}
                  {!module.type && (
                    <a
                      href={module.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-outline-primary"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Open file
                    </a>
                  )}
                </>
              ) : null}
            </MediaTile>
              </Col>
              <Col xs="auto">
            <MediaTile
              label="Thumbnail"
              iconClass="ri-image-line"
              layout="thumbnail"
              hasContent={hasThumbnail}
              disabled={thumbnailUploadDisabled}
              removeDisabled={isTargetBusy('thumbnail')}
              isRemoving={module.removingTarget === 'thumbnail'}
              isUploading={module.thumbnailUploadStatus === 'uploading'}
              uploadProgress={module.thumbnailUploadProgress ?? 0}
              uploadLabel="Uploading thumbnail…"
              emptyHint={hasTitle ? 'Drop cover image (any orientation)' : 'Add a title first'}
              replaceHint={hasTitle ? 'Drop or click to replace' : 'Add a title first'}
              isDragActive={isThumbnailDragActive}
              isDragAccept={isThumbnailDragAccept}
              isDragReject={isThumbnailDragReject}
              dragAcceptLabel="Drop thumbnail"
              dragRejectLabel="Use an image file"
              getRootProps={getThumbnailProps}
              getInputProps={getThumbnailInputProps}
              onRemoveClick={hasThumbnail ? () => requestRemove('thumbnail') : undefined}
            >
              {module.thumbnailPreview ? (
                <ThumbnailPreviewFrame src={module.thumbnailPreview} alt="Module thumbnail" />
              ) : module.thumbnail ? (
                <ThumbnailPreviewFrame>
                  <div className="text-center px-2 text-muted">
                    <i className="ri-image-add-line fs-2 d-block mb-2" />
                    <p className="mb-0 small text-truncate">{module.thumbnail.name}</p>
                    {!contentId && (
                      <Badge color="warning" className="mt-2 fw-normal">Pending save</Badge>
                    )}
                  </div>
                </ThumbnailPreviewFrame>
              ) : null}
            </MediaTile>
              </Col>
            </Row>
          </Col>
          </>
          <Col md={12}>
            <FormGroup>
              <Label>
                Questions
                <Button type="button" color="link" size="sm" className="ms-2 p-0" onClick={addQuestion}>
                  <i className="ri-add-line me-1"></i>Add Question
                </Button>
              </Label>
              {questions.length === 0 ? (
                <p className="text-muted small mb-0">
                  Optional. Users answer after media completes. Answers can become diary entries.
                </p>
              ) : (
                <div className="border rounded p-2 bg-light">
                  {questions.map((q, qIdx) => (
                    <div
                      key={qIdx}
                      className="mb-3 pb-3"
                      style={qIdx < questions.length - 1 ? { borderBottom: '1px solid #dee2e6' } : undefined}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <strong className="small">Question {qIdx + 1}</strong>
                        <Button
                          type="button"
                          color="danger"
                          size="sm"
                          outline
                          onClick={() => removeQuestion(qIdx)}
                        >
                          <i className="ri-close-line"></i>
                        </Button>
                      </div>
                      <Input
                        type="text"
                        placeholder="Prompt (e.g. What came up for you?)"
                        value={q.prompt}
                        onChange={(e) => updateQuestion(qIdx, 'prompt', e.target.value)}
                        className="mb-2"
                      />
                      <Input
                        type="textarea"
                        rows={2}
                        placeholder="Instruction (optional)"
                        value={q.instruction || ''}
                        onChange={(e) => updateQuestion(qIdx, 'instruction', e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </FormGroup>
          </Col>
          <Col md={12}>
            <FormGroup>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Label className="mb-0 d-block">Upload transcript (.srt only)</Label>
                {hasTranscript && (
                  <Button
                    type="button"
                    color="link"
                    size="sm"
                    className="p-0 text-danger"
                    onClick={clearTranscript}
                    title="Remove transcript"
                  >
                    <i className="ri-delete-bin-line me-1" />
                    Remove
                  </Button>
                )}
              </div>
              <div
                {...getTranscriptProps()}
                className="border border-2 border-dashed rounded p-2 text-center mb-2 small"
                style={{ cursor: 'pointer' }}
              >
                <input {...getTranscriptInputProps()} />
                {module.transcriptFileName ? (
                  <>
                    <i className="ri-file-text-line me-1 text-success"></i>
                    <span className="fw-semibold">{module.transcriptFileName}</span>
                    {module.transcriptTimestamps && module.transcriptTimestamps.length > 0 && (
                      <span className="text-muted d-block">({module.transcriptTimestamps.length} timestamps)</span>
                    )}
                  </>
                ) : (module.transcript?.trim() || (module.transcriptTimestamps && module.transcriptTimestamps.length > 0)) ? (
                  <>
                    <i className="ri-file-text-line me-1 text-success"></i>
                    <span className="fw-semibold">Transcript saved</span>
                    <span className="text-muted d-block">
                      {module.transcriptTimestamps?.length ?? 0} timestamp(s) — drop new .srt to replace
                    </span>
                  </>
                ) : (
                  <>
                    <i className="ri-upload-cloud-line me-1"></i>
                    Drop .srt or click to browse
                  </>
                )}
              </div>
            </FormGroup>
          </Col>
          <Col md={12}>
            <FormGroup>
              <Label>Transcript</Label>
              <Input
                type="textarea"
                rows={3}
                placeholder="Module transcript (or upload .srt above)"
                value={module.transcript}
                onChange={(e) => onUpdate('transcript', e.target.value)}
              />
            </FormGroup>
          </Col>
          <Col md={12}>
            <FormGroup>
              <Label className="fw-semibold">Reflection</Label>
              <Input
                type="text"
                placeholder="Reflection title (e.g. Today's Reflection)"
                value={reflection.title || ''}
                onChange={(e) => updateReflection('title', e.target.value)}
                className="mb-2"
              />
              <Input
                type="textarea"
                rows={2}
                placeholder="Reflection description"
                value={reflection.description || ''}
                onChange={(e) => updateReflection('description', e.target.value)}
                className="mb-2"
              />
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Label className="mb-0 small text-muted">Bullet points</Label>
                <Button type="button" color="link" size="sm" className="p-0" onClick={addBulletPoint}>
                  <i className="ri-add-line me-1"></i>Add bullet
                </Button>
              </div>
              {bulletPoints.length === 0 ? (
                <p className="text-muted small mb-0">Optional reflection prompts shown to users for this day.</p>
              ) : (
                <div className="border rounded p-2 bg-light">
                  {bulletPoints.map((point, bpIdx) => (
                    <div key={bpIdx} className="d-flex gap-2 mb-2">
                      <Input
                        type="text"
                        placeholder={`Bullet point ${bpIdx + 1}`}
                        value={point}
                        onChange={(e) => updateBulletPoint(bpIdx, e.target.value)}
                      />
                      <Button
                        type="button"
                        color="danger"
                        size="sm"
                        outline
                        onClick={() => removeBulletPoint(bpIdx)}
                      >
                        <i className="ri-close-line"></i>
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </FormGroup>
          </Col>
        </Row>
      </CardBody>

      <Modal isOpen={confirmRemove !== null} toggle={() => setConfirmRemove(null)} centered size="sm">
        <ModalHeader toggle={() => setConfirmRemove(null)}>
          Remove {confirmRemove === 'file' ? 'media' : 'thumbnail'}?
        </ModalHeader>
        <ModalBody className="small">
          {confirmRemove === 'file' ? (
            <>
              This will permanently delete the audio/video for <strong>Day {index + 1}</strong> from the server.
              Users will no longer be able to play it until you upload again.
            </>
          ) : (
            <>
              Remove the thumbnail for <strong>Day {index + 1}</strong>? You can upload a new one anytime.
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setConfirmRemove(null)}>
            Cancel
          </Button>
          <Button color="danger" onClick={confirmRemoveMedia}>
            Remove
          </Button>
        </ModalFooter>
      </Modal>
    </Card>
  );
};

export default ModuleItemCard;
